import React, { Fragment, useState, useEffect, useMemo } from "react";
import { Menu, Transition } from "@headlessui/react";
import { ReactSVG } from "react-svg";
import CustomInput from "../../../../common/CustomInput";
import Button from "../../../../common/Button";
import { formatToYYYY_MMM_DD, formatTimeFromISO } from "../../../../../helpers/functions";
import { ClinicalServiceTypes } from "../../../text/data";
import { followUpTopicOptions, reasonStartOptions } from "./data";

const FollowUpCard = ({
  selectedCard,
  setSelectedCard,
  followUpTitle,
  clinicalServiceName,
  followUpDate,
  reasonStart,
  explaination,
  followUpType,
  showARResultCard,
  isFlipping,
  isCardFlipped,
  shouldShowFlipArrow,
  handleArrowClick,
  handleDateChange,
  handleReasonStartChange,
  handleExplainationChange,
  isDropdownDisabled,
  setShowARResultCard,
  setIsCardFlipped,
  clearFollowUpSelection,
  restoreFollowUpSelection,
  halfBorderSection,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalValues, setOriginalValues] = useState({
    date: null,
    reason: null,
    explanation: null,
    followUpType: [],
  });

  useEffect(() => {
    if (!followUpDate && !reasonStart) {
      setIsEditMode(false);

      if (typeof setShowARResultCard === "function") {
        setShowARResultCard(false);
      }

      if (typeof setIsCardFlipped === "function") {
        setIsCardFlipped(false);
      }
    }
  }, [followUpDate, reasonStart]);

  // Helper function to set icon size to 80px
  const setIconSize = (svg) => {
    svg.setAttribute('width', '80px');
    svg.setAttribute('height', '80px');
  };

  // Helper function to check if follow-up was declined (shows cross icon)
  const isFollowUpDeclined = () => {
    return (
      reasonStart &&
      reasonStart !== "Pharmacist" &&
      reasonStart !== "PharmacyBooking" &&
      followUpDate === null
    );
  };

  // Helper function to get the reason label for display
  const getReasonLabel = () => {
    if (reasonStart === "Other" && explaination) {
      return explaination;
    }
    const option = reasonStartOptions.find((option) => option.value === reasonStart);
    return option?.label || "No Follow Up - Other";
  };

  const saveOriginalValues = () => {
    setOriginalValues({
      date: followUpDate,
      reason: reasonStart,
      explanation: explaination,
      followUpType: Array.isArray(followUpType) ? [...followUpType] : [],
    });
  };

  const normalizeDateForCompare = (d) => {
    if (d == null || d === "") return "";
    try {
      const t = new Date(d).getTime();
      return Number.isNaN(t) ? String(d) : String(t);
    } catch {
      return String(d);
    }
  };

  const followUpTypesEqual = (a, b) => {
    const aa = Array.isArray(a) ? [...a].sort() : [];
    const bb = Array.isArray(b) ? [...b].sort() : [];
    if (aa.length !== bb.length) return false;
    return aa.every((v, i) => v === bb[i]);
  };

  const hasFollowUpEditChanges = useMemo(() => {
    if (!isEditMode) return false;
    return (
      normalizeDateForCompare(followUpDate) !==
        normalizeDateForCompare(originalValues.date) ||
      (reasonStart ?? null) !== (originalValues.reason ?? null) ||
      String(explaination ?? "").trim() !==
        String(originalValues.explanation ?? "").trim() ||
      !followUpTypesEqual(followUpType, originalValues.followUpType)
    );
  }, [
    isEditMode,
    followUpDate,
    reasonStart,
    explaination,
    followUpType,
    originalValues,
  ]);

  const handleEdit = () => {
    saveOriginalValues();
    setIsEditMode(true);
    setShowARResultCard(false);
    setIsCardFlipped(false);
  };

  const handleCancel = () => {
    if (typeof restoreFollowUpSelection === "function") {
      restoreFollowUpSelection(originalValues);
    }
    setIsEditMode(false);
    if (originalValues.date || originalValues.reason) {
      setShowARResultCard(true);
    }
  };

  const handleUpdate = () => {
    setIsEditMode(false);
    if (followUpDate || reasonStart) {
      setShowARResultCard(true);
    }
  };

  const shouldRenderResultCard =
    !isEditMode &&
    !isFlipping &&
    Boolean(showARResultCard) &&
    Boolean(followUpDate || reasonStart);

  return (
    <div
      className={`relative flex flex-row h-[516px] w-[525px] pl-[12px] pt-[14px] rounded-[12px] gap-[10px] border-t-[6px] border-l-[6px] pr-[16px] ${
        selectedCard === 1
          ? "border-REELTeal"
          : "border-blue-200"
      }`}
    >
      {halfBorderSection(1)}
      <div
        className="flex flex-col justify-center h-[480px] w-[480px] rounded-[12px]  border-b-gray-100 border-x-gray-100 border-[1px] shadow-lg shadow-gray-400 z-[1]"
        onClick={() => {
          setSelectedCard(1);
        }}
      >
        {/* Show flip arrow for non-AllergicRhinitis when conditions are met */}
        {shouldShowFlipArrow() && !isCardFlipped && !Boolean(followUpDate || reasonStart) && (
          <div className="absolute bottom-4 right-4 cursor-pointer mr-11 mb-7">
            <button onClick={handleArrowClick}>
              <ReactSVG src="/icons/arrow-right.svg" />
            </button>
          </div>
        )}

        {/* Input Form - Show when result card is NOT shown, during flipping, or in edit mode */}
        {(isEditMode || !shouldRenderResultCard) && (
            <div className={`relative flex flex-col px-12 pt-5 pb-2 gap-5 ${
              (clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis 
                ? reasonStart === "Other"
                : Array.isArray(followUpType) && followUpType.includes("Other"))
                ? "justify-start min-h-[320px]"
                : "justify-center h-[320px]"
            }`}>
                <div className="flex flex-col gap-2">
                  <h2 className="text-xl font-bold flex justify-center text-teal-300">
                    {followUpTitle} Follow Up
                  </h2>
                  <p className="flex justify-center text-gray-800 font-semibold text-base">
                    {clinicalServiceName ===
                    ClinicalServiceTypes.AllergicRhinitis
                      ? "Book 2 week follow up appointment"
                      : "Book Follow up Appointment (Optional)"}
                  </p>
                </div>

              <div className="flex flex-col text-gray-800 gap-2">
                {/* Date picker */}
                <div className="w-full">
                  <CustomInput
                    name="Follow up date"
                    className="col-span-12"
                    label="Appointment Date & Time"
                    type="datetime"
                    value={followUpDate || ""}
                    onChange={handleDateChange}
                  />
                </div>

                {/* Dropdown - different based on service type */}
                <div className="w-full">
                  {clinicalServiceName ===
                  ClinicalServiceTypes.AllergicRhinitis ? (
                    <CustomInput
                      options={reasonStartOptions}
                      name="reason"
                      className="col-span-12 py-[22px]"
                      label=""
                      type="dropdown"
                      placeholder="reason if not booking follow up"
                      value={reasonStart || ""}
                      onChange={(e) =>
                        handleReasonStartChange(e.value)
                      }
                      shortMenuHeight={true}
                      disabled={isEditMode ? false : isDropdownDisabled()}
                      closeOnSelectValues={["Other", "Refused", "NotSpecified"]}
                    />
                  ) : (
                    <div className="w-full py-5 border border-gray-200 rounded-md bg-teal-25 flex items-center justify-center">
                      <span className="text-gray-800 font-semibold">Education Modules Coming Soon</span>
                    </div>
                  )}
                </div>

                {/* Other explanation field - shows when "Other" is selected */}
                {/* For AllergicRhinitis check reasonStart, for other services check if "Other" is in followUpType array */}
                {(clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis 
                  ? reasonStart === "Other"
                  : Array.isArray(followUpType) && followUpType.includes("Other")) && (
                  <div className="w-full flex flex-col text-gray-800 gap-[4px] mt-2">
                    
                    <CustomInput
                      name="explanation"
                      className="col-span-12 py-[22px]"
                      label=""
                      type="text"
                      placeholder="Enter your explanation"
                      value={explaination || ""}
                      onChange={(e) =>
                        handleExplainationChange(e.target.value)
                      }
                    />
                  </div>
                )}
              </div>

              {/* Cancel and Update buttons - Positioned absolutely at bottom, only in edit mode */}
              {isEditMode && (
                <div className="absolute -bottom-16 right-12 flex flex-row gap-2 justify-end">
                  <Button
                    className="!px-3 !py-1.5 !text-sm min-w-[72px] max-h-[36px] !rounded-[24px]"
                    text="Cancel"
                    type="primary-outline"
                    onClick={handleCancel}
                  />
                  <Button
                    className="!px-3 !py-1.5 !text-sm min-w-[72px] max-h-[36px] !rounded-[24px]"
                    text="Update"
                    type="primary"
                    disabled={!hasFollowUpEditChanges}
                    onClick={handleUpdate}
                  />
                </div>
              )}
            </div>
          )}

        {/* Result Card - Show when conditions are met, not flipping, and not in edit mode */}
        {shouldRenderResultCard && (
          <>
            <div className="absolute top-[30px] right-[50px] cursor-pointer z-1">
              <Menu
                as="div"
                className="relative inline-block text-left"
              >
                {({ open }) => (
                  <>
                    <div>
                      <Menu.Button>
                        <ReactSVG
                          src={`/icons/big-triple-dot.svg`}
                          className={`flex text-black py-2 px-4 rounded-md  ${
                            open ? "bg-gray-100" : ""
                          }`}
                        />
                      </Menu.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform translate-y-2 opacity-0 scale-95"
                      enterTo="transform translate-y-0 opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform translate-y-0 opacity-100 scale-100"
                      leaveTo="transform translate-y-2 opacity-0 scale-95"
                    >
                      <Menu.Items
                        className={
                          "absolute right-0 mr-2 w-24 origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 "
                        }
                      >
                        <Menu.Item>
                          <button
                            className="block w-full text-left px-3 py-3 border-b border-b-neutral-70 hover:bg-gray-300"
                            onClick={handleEdit}
                          >
                            Edit
                          </button>
                        </Menu.Item>
                        <Menu.Item>
                          <button
                            className="block w-full text-left px-3 py-3 border-b border-b-neutral-70 hover:bg-gray-300"
                            onClick={() => {
                              clearFollowUpSelection();
                              setShowARResultCard(false);
                              setIsCardFlipped(false);
                              setIsEditMode(false);
                            }}
                          >
                            Delete
                          </button>
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </>
                )}
              </Menu>
            </div>
            <div className="flex flex-col h-[320px] pt-5 pb-2 gap-4 px-12 justify-center items-center">
              <div className="flex justify-center">
                {clinicalServiceName ===
                ClinicalServiceTypes.AllergicRhinitis ? (
                  <>
                    {isFollowUpDeclined() && (
                      <ReactSVG
                        src={`/icons/error-toast.svg`}
                        beforeInjection={setIconSize}
                      />
                    )}
                    {(reasonStart === "Pharmacist" ||
                      reasonStart === "PharmacyBooking" ||
                      followUpDate !== null) && (
                      <ReactSVG
                        src={`/icons/follow-up.svg`}
                        beforeInjection={setIconSize}
                      />
                    )}
                  </>
                ) : (
                  <ReactSVG
                    src={`/icons/follow-up.svg`}
                    beforeInjection={setIconSize}
                  />
                )}
              </div>
              <h2 className="text-xl font-bold flex justify-center text-teal-300">
                {clinicalServiceName ===
                ClinicalServiceTypes.AllergicRhinitis ? (
                  <>
                    {followUpTitle}{" "}
                    Follow Up
                    {isFollowUpDeclined() ? "" : " Booked"}
                  </>
                ) : (
                  "Follow Up Appointment Booked"
                )}
              </h2>
              {clinicalServiceName ===
              ClinicalServiceTypes.AllergicRhinitis ? (
                <>
                  {followUpDate && (
                    <div className="flex flex-col text-gray-800 gap-[4px] justify-center items-center">
                      <p className="text-base font-medium">
                        Follow up booked:{" "}
                        {formatToYYYY_MMM_DD(followUpDate)}, {formatTimeFromISO(followUpDate)}
                      </p>
                    </div>
                  )}
                  {reasonStart === "PharmacyBooking" && (
                    <div className="flex flex-col text-gray-800 gap-[4px] justify-center items-center">
                      <p className="text-base font-medium">
                        Using Pharmacy's booking system
                      </p>
                    </div>
                  )}
                  {isFollowUpDeclined() && (
                    <div className="flex flex-col text-gray-800 gap-[4px] justify-center items-center">
                      <p className="text-base font-medium">
                        {getReasonLabel()}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col text-gray-800 gap-[4px] justify-center items-center">
                  <p className="text-base font-medium">
                    Follow up booked{" "}
                    {formatToYYYY_MMM_DD(followUpDate)}, {formatTimeFromISO(followUpDate)}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FollowUpCard;